import * as React from "react";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { getRolesData, getUser } from '../../store/actions/User';
import { getMFASetup, verifyMFASetup } from '../../services/auth';
import NeatcarButton from "../../components/NeatcarButton";
import neatcarLogo from '../../assets/images/neatcar-logo.png';

const MFASetup = () => {
  const { register, handleSubmit, formState: { errors }, setValue } = useForm();
  const [loading, setLoading] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState(null);
  const [qrCodeSecret, setQrCodeSecret] = useState(null);
  const [verificationError, setVerificationError] = useState(false);
  const [loadingSetup, setLoadingSetup] = useState(true);
  const { t } = useTranslation('login');

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user, authenticated, roles } = useSelector(store => store.session);

  useEffect(() => {
    // Fetch MFA setup data (QR code)
    getMFASetup().subscribe({
      next: (data) => {
        // Assuming the backend returns { qrCodeUrl: '...', secret: '...' } or similar
        if (data.qrCodeDataURI) {
          setQrCodeUrl(data.qrCodeDataURI);
        } 
        if (data.totpSecret) {
          setQrCodeSecret(data.totpSecret);
        }
        setLoadingSetup(false);
      },
      error: (error) => {
        console.error('Error fetching MFA setup:', error);
        setLoadingSetup(false);
      }
    });
  }, [user]);

  useEffect(() => {
    if (authenticated && roles && roles.length > 0 && user?.hasTOTP) {
      // User already has TOTP set up, redirect to dashboard
      const defaultRoute = roles.find(role => role.role === "ROLE_BUSINESS") ? "/team" : "/Insurer";
      navigate(defaultRoute, { replace: true });
    }
  }, [authenticated, user, roles, navigate]);

  const onSubmit = async (data) => {
    setLoading(true);
    setVerificationError(false);
    
    verifyMFASetup(data.totpCode, qrCodeSecret).subscribe({
      next: () => {
        // MFA setup successful, refresh user data and redirect
        dispatch(getUser());
        dispatch(getRolesData()).then(() => {
          const defaultRoute = roles?.find(role => role.role === "ROLE_BUSINESS") ? "/team" : "/Insurer";
          navigate(defaultRoute, { replace: true });
        });
      },
      error: (error) => {
        console.error('Error verifying MFA setup:', error);
        setVerificationError(true);
        setLoading(false);
      }
    });
  };

  return (
    <>
      <div className="min-h-full flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <img
            className="mx-auto h-12 w-auto"
            src={neatcarLogo}
            alt="Neatcar"
          />
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
            <div className={'text-center py-4 mb-6'}>
              <h1 className={'text-2xl font-bold'}>{t('mfaSetupTitle')}</h1>
              <p className={'text-sm text-gray-500 mt-2'}>{t('mfaSetupSubtitle')}</p>
            </div>

            {loadingSetup ? (
              <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-neatcar"></div>
              </div>
            ) : (
              <>
                {/* Instructions */}
                <div className="mb-6">
                  <p className="text-sm text-gray-700 whitespace-pre-line text-center">
                    {t('mfaSetupInstructions')}
                  </p>
                </div>

                {/* QR Code Display */}
                <div className="flex justify-center mb-6">
                  <div className="bg-white p-4 rounded-lg border-2 border-gray-200">
                    {qrCodeUrl ? (
                      <img
                        src={qrCodeUrl}
                        alt="QR Code"
                        className="w-48 h-48 mx-auto"
                      />
                    ) : (
                      <div className="w-48 h-48 bg-gray-100 flex items-center justify-center">
                        <p className="text-gray-400 text-sm">Loading QR code...</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Manual entry option (if secret is available) */}
                {qrCodeSecret && (
                  <div className="mb-6 p-3 bg-gray-50 rounded-md">
                    <p className="text-xs text-gray-600 text-center mb-2">
                      Can't scan? Enter this code manually:
                    </p>
                    <p className="text-sm font-mono text-center font-semibold text-gray-800">
                      {qrCodeSecret}
                    </p>
                  </div>
                )}

                {/* Code Input Form */}
                <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
                  <div>
                    <label htmlFor="totpCode" className="block text-sm font-medium text-gray-700 mb-2">
                      {t('totpCode')}
                    </label>
                    <div className="mt-1">
                      <input
                        {...register("totpCode", { 
                          required: { value: true, message: t('required') }, 
                          pattern: { 
                            value: /^\d{6}$/, 
                            message: t('invalidTotpCode')
                          },
                          maxLength: {
                            value: 6,
                            message: t('codeMustBe6Digits')
                          },
                          minLength: {
                            value: 6,
                            message: t('codeMustBe6Digits')
                          }
                        })}
                        id="totpCode"
                        name="totpCode"
                        type="text"
                        inputMode="numeric"
                        maxLength="6"
                        autoComplete="one-time-code"
                        placeholder="000000"
                        className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-neatcar focus:border-neatcar sm:text-sm text-center text-2xl tracking-widest"
                        onInput={(e) => {
                          // Only allow digits and limit to 6
                          const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                          e.target.value = value;
                          setValue('totpCode', value, { shouldValidate: true });
                        }}
                      />
                      {errors.totpCode && (
                        <span className="text-red-500 text-xs mt-1 block">{errors.totpCode.message}</span>
                      )}
                      {verificationError && (
                        <span className="text-red-500 text-xs mt-1 block">{t('mfaSetupVerificationError')}</span>
                      )}
                    </div>
                  </div>

                  <div>
                    <NeatcarButton
                      type={'submit'}
                      loading={loading}
                      title={t('mfaSetupComplete')}
                    />
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

export default MFASetup;
